import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, ArrowDownToLine, ArrowUpFromLine, IndianRupee, Package, ImagePlus, Pencil, Search, AlertTriangle, ArrowUpDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, inr, imgUrl, money, downloadExcel } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const emptyProduct = { name: "", sku: "", description: "", image_url: "", unit: "pcs", type: "product", category: "", pcs_per_ctn: 0, cost_price: 0, b2b_price: 0, sell_price: 0, min_stock: 0 };

export default function Inventory() {
  const { user } = useAuth();
  const canManage = ["admin", "staff", "stock_head"].includes(user?.role);
  const canMove = canManage || user?.role === "stock_team";
  const canEditProduct = canManage || user?.role === "stock_team";
  const [warehouses, setWarehouses] = useState([]);
  const [firmFilter, setFirmFilter] = useState("all");
  const [firms, setFirms] = useState([]);
  const [itemTab, setItemTab] = useState("products");
  const [units, setUnits] = useState([]);
  const [newUnit, setNewUnit] = useState("");
  const [activeWh, setActiveWh] = useState("all");
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);
  const [movSearch, setMovSearch] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const [pg, setPg] = useState({ page: 1, size: 10 });
  const [sales, setSales] = useState([]);
  const [activityTab, setActivityTab] = useState("movements");
  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState({ name: "", location: "" });
  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [editProdId, setEditProdId] = useState(null);
  const [movOpen, setMovOpen] = useState(false);
  const [movForm, setMovForm] = useState({ product_id: "", warehouse_id: "", type: "in", qty: 1, rate: 0, note: "", tag: "", out_type: "sold", transfer_to: "" });
  const [movSaving, setMovSaving] = useState(false);
  const [tagColor, setTagColor] = useState("#4F46E5");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [wh, st, mv, tg, sl, fm, un] = await Promise.all([
      API.get("/warehouses"),
      API.get("/inventory/stock"),
      API.get("/inventory/movements"),
      API.get("/inventory/tags"),
      API.get("/inventory/sales"),
      API.get("/firms"),
      API.get("/inventory/units"),
    ]);
    setWarehouses(wh.data);
    setStock(st.data);
    setMovements(mv.data);
    setTags(tg.data);
    setSales(sl.data);
    setFirms(fm.data);
    setUnits(un.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const addWarehouse = async () => {
    if (!whForm.name.trim()) return toast.error("Warehouse name is required");
    await API.post("/warehouses", whForm);
    setWhOpen(false);
    setWhForm({ name: "", location: "" });
    toast.success("Warehouse added");
    load();
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await API.post("/upload", fd);
    setProdForm((p) => ({ ...p, image_url: data.url }));
    toast.success("Image uploaded");
  };

  const saveProduct = async () => {
    if (!prodForm.name.trim()) return toast.error("Product name is required");
    const payload = { ...prodForm, cost_price: Number(prodForm.cost_price) || 0, b2b_price: Number(prodForm.b2b_price) || 0, sell_price: Number(prodForm.sell_price) || 0, min_stock: Number(prodForm.min_stock) || 0 };
    if (editProdId) {
      await API.patch(`/products/${editProdId}`, payload);
      toast.success("Product updated");
    } else {
      await API.post("/products", payload);
      toast.success("Product added");
    }
    setProdOpen(false);
    setProdForm(emptyProduct);
    setEditProdId(null);
    load();
  };

  const removeProduct = async (id) => {
    await API.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const openMovement = (product, type) => {
    setMovForm({
      product_id: product.id,
      warehouse_id: activeWh !== "all" ? activeWh : warehouses[0]?.id || "",
      type,
      qty: 1,
      rate: type === "in" ? product.cost_price : product.sell_price,
      note: "",
      tag: "",
      out_type: "sold",
      transfer_to: "",
      unit_mode: "",
    });
    setMovOpen(true);
  };

  const saveMovement = async () => {
    if (movSaving) return;
    if (!movForm.warehouse_id) return toast.error("Select a warehouse");
    if (movForm.type === "out" && movForm.out_type === "transfer") {
      if (!movForm.transfer_to) return toast.error("Select the destination warehouse");
      if (movForm.transfer_to === movForm.warehouse_id) return toast.error("Destination must be a different warehouse");
    }
    setMovSaving(true);
    try {
      const tagName = movForm.tag.trim();
      if (tagName && !tags.some((t) => t.name === tagName)) {
        await API.post("/inventory/tags", { name: tagName, color: tagColor });
      }
      await API.post("/inventory/movements", { ...movForm, tag: tagName, qty: Number(movForm.qty) || 0, rate: Number(movForm.rate) || 0 });
      setMovOpen(false);
      toast.success(movForm.type === "in" ? "Inbound recorded" : movForm.out_type === "transfer" ? "Transfer recorded — stock added to destination" : movForm.out_type === "sold_to_b2b" ? "Dispatched to B2B — challan created, pending accounts receipt" : "Sale recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setMovSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleStock = stock.filter(
    (p) => (p.type || "product") !== "service"
      && (!q || p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q))
  );
  const categories = [...new Set(visibleStock.map((p) => p.category).filter(Boolean))];
  const qtyFor = (p) => (activeWh === "all" ? p.total_stock : p.stock_by_warehouse?.[activeWh] ?? 0);
  const lowStockCount = visibleStock.filter((p) => p.low_stock).length;
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const sortedStock = [...visibleStock].sort((a, b) => {
    if (!sort.key) return 0;
    const val = (p) =>
      sort.key === "stock" ? qtyFor(p) : sort.key === "value" ? qtyFor(p) * (p.cost_price || 0) : p[sort.key];
    const va = val(a);
    const vb = val(b);
    if (typeof va === "string") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sort.dir === "asc" ? va - vb : vb - va;
  });
  const showCost = ["admin", "staff", "stock_head"].includes(user?.role);
  const showB2B = ["admin", "staff", "stock_head", "accountant"].includes(user?.role);
  const showSell = ["admin", "staff", "sales", "accountant"].includes(user?.role);
  const showValue = ["admin", "staff", "stock_head"].includes(user?.role);
  const showSold = ["admin", "staff", "sales", "stock_team", "stock_head", "accountant"].includes(user?.role);
  const showMovRate = ["admin", "staff", "accountant"].includes(user?.role) && !(user?.role === "stock_head" && movForm.type === "out");
  const tagStyle = (name) => {
    const c = tags.find((t) => t.name === name)?.color || "#64748B";
    return { backgroundColor: c + "1A", color: c, borderColor: c + "66" };
  };
  const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
  const recentMovements = movements.filter((m) => {
    if (new Date(m.created_at).getTime() < twoDaysAgo) return false;
    const q2 = movSearch.trim().toLowerCase();
    if (!q2) return true;
    return (m.tag || "").toLowerCase().includes(q2) || (m.product_name || "").toLowerCase().includes(q2) || (m.note || "").toLowerCase().includes(q2);
  });
  const totalValue = visibleStock.reduce((a, p) => a + qtyFor(p) * (p.cost_price || 0), 0);
  const whName = (id) => warehouses.find((w) => w.id === id)?.name || "warehouse";
  const lowAlerts = stock.filter((p) => (p.low_stock_warehouses || []).length > 0);
  const pagedStock = sortedStock.slice((pg.page - 1) * pg.size, pg.page * pg.size);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Warehouse Inventory</h1>
          <p className="text-sm text-slate-500">Track inbound, outbound and stock value per warehouse</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search products or SKU..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 pl-8" data-testid="product-search-input" />
          </div>
          {canManage && (
            <Button variant="outline" onClick={() => setWhOpen(true)} data-testid="add-warehouse-button">
              <Plus className="mr-1 h-4 w-4" /> Warehouse
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadExcel(`/inventory/export${activeWh !== "all" ? `?warehouse_id=${activeWh}` : ""}`, "stock.xlsx")} data-testid="inventory-export-button">
            <FileSpreadsheet className="mr-1 h-4 w-4" /> Excel
          </Button>
          {canEditProduct && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setProdForm(emptyProduct); setEditProdId(null); setProdOpen(true); }} data-testid="add-product-button">
              <Plus className="mr-1 h-4 w-4" /> Product
            </Button>
          )}
        </div>
      </div>

      {lowAlerts.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="low-stock-alerts">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4" /> Low stock alerts ({lowAlerts.length})
          </div>
          <div className="mt-2 space-y-1">
            {lowAlerts.map((p) =>
              p.low_stock_warehouses.map((wh) => (
                <div key={`${p.id}-${wh}`} className="text-xs text-amber-700" data-testid={`low-stock-${p.id}-${wh}`}>
                  <span className="font-medium">{p.name}</span> — {whName(wh)}: {(p.stock_by_warehouse || {})[wh] ?? 0} {p.unit || "pcs"} left (min {p.min_stock})
                </div>
              )),
            )}
          </div>
          <p className="mt-2 text-[11px] text-amber-600/70">Set per-product thresholds via Edit Product → Min stock alert.</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="item-tabs">
        {[["products", "Products"], ["category", "Category"], ["units", "Units"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setItemTab(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${itemTab === k ? "bg-indigo-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid={`item-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {itemTab === "category" && (
        <div className="rounded-lg border border-slate-200 bg-white" data-testid="category-panel">
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Categories</div>
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Items</TableHead><TableHead className="text-right">Stock Qty</TableHead></TableRow></TableHeader>
            <TableBody data-testid="category-table">
              {categories.map((c) => (
                <TableRow key={c} data-testid={`category-row-${c.replace(/\W+/g, "-")}`}>
                  <TableCell className="font-medium">{c}</TableCell>
                  <TableCell className="text-right">{stock.filter((p) => p.category === c).length}</TableCell>
                  <TableCell className="text-right">{stock.filter((p) => p.category === c).reduce((a, p) => a + (p.total_stock || 0), 0)}</TableCell>
                </TableRow>
              ))}
              {categories.length === 0 && <TableRow><TableCell colSpan={3}><EmptyState compact title="No categories yet" hint="Set a category on a product from its Add/Edit dialog." /></TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {itemTab === "units" && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="units-panel">
          <div className="mb-3 text-sm font-semibold text-slate-900">Units</div>
          <div className="flex flex-wrap gap-2" data-testid="units-list">
            {units.map((u) => <Badge key={u} variant="outline" className="px-3 py-1 text-sm" data-testid={`unit-chip-${u}`}>{u}</Badge>)}
            {units.length === 0 && <EmptyState compact title="No units yet" />}
          </div>
          {["admin", "staff", "stock_head"].includes(user?.role) && (
            <div className="mt-3 flex gap-2">
              <Input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="New unit (e.g. dozen, metre)" className="h-9 w-56" data-testid="new-unit-input" />
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-unit-button"
                onClick={async () => {
                  if (!newUnit.trim()) return;
                  try {
                    await API.post("/inventory/units", { name: newUnit.trim() });
                    setUnits((u) => [...new Set([...u, newUnit.trim()])].sort());
                    setNewUnit("");
                    toast.success("Unit added");
                  } catch (e) { toast.error(e.response?.data?.detail || "Could not add unit"); }
                }}>Add Unit</Button>
            </div>
          )}
        </div>
      )}

      {itemTab === "products" && (<>
      {["admin", "accountant"].includes(user?.role) && firms.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="firm-tabs">
          {[{ k: "all", label: "All Firms" }, { k: "firm1", label: (firms.find((f) => /b2c/i.test(f.name)) || firms[0] || {}).name || "Company 1" }, { k: "firm2", label: (firms.find((f) => /b2b/i.test(f.name)) || firms[1] || {}).name || "Company 2" }].map((f) => (
            <button
              key={f.k}
              onClick={() => { setFirmFilter(f.k); setActiveWh("all"); }}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-150 ${firmFilter === f.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
              data-testid={`firm-tab-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="warehouse-tabs">
        <button
          onClick={() => setActiveWh("all")}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${activeWh === "all" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
          data-testid="warehouse-tab-all"
        >
          All Warehouses
        </button>
        {(firmFilter === "all" ? warehouses : warehouses.filter((w) => w.firm_id === (firmFilter === "firm1" ? (firms.find((f) => /b2c/i.test(f.name)) || firms[0])?.id : (firms.find((f) => /b2b/i.test(f.name)) || firms[1])?.id))).map((w) => (
          <button
            key={w.id}
            onClick={() => setActiveWh(w.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${activeWh === w.id ? "bg-indigo-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid={`warehouse-tab-${w.id}`}
          >
            {w.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="border-slate-200" data-testid="inv-stat-products">
          <CardContent className="flex items-center gap-3 p-4">
            <Package className="h-8 w-8 text-indigo-500" />
            <div><div className="text-xs uppercase text-slate-500">Products</div><div className="font-heading text-xl font-bold">{visibleStock.length}</div></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="inv-stat-low-stock">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <div><div className="text-xs uppercase text-slate-500">Low Stock Products</div><div className="font-heading text-xl font-bold">{lowStockCount}</div></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="inv-stat-value">
          <CardContent className="flex items-center gap-3 p-4">
            <IndianRupee className="h-8 w-8 text-indigo-500" />
            <div><div className="text-xs uppercase text-slate-500">Stock Value (cost)</div><div className="font-heading text-xl font-bold">{showValue ? inr(totalValue) : "—"}</div></div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="sort-name">Product <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Unit</TableHead>
              {showCost && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("cost_price")} data-testid="sort-cost">Cost <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>}
              {showB2B && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("b2b_price")} data-testid="sort-b2b">B2B Purchase <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>}
              {showSell && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("sell_price")} data-testid="sort-sell">Sell <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>}
              <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("stock")} data-testid="sort-stock">Stock <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>
              {showValue && <TableHead className="cursor-pointer select-none text-right" onClick={() => toggleSort("value")} data-testid="sort-value">Value <ArrowUpDown className="inline h-3 w-3 text-slate-400" /></TableHead>}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="stock-table">
            {pagedStock.map((p) => (
              <TableRow key={p.id} className={p.low_stock ? "bg-amber-50 hover:bg-amber-100/70" : ""} data-testid={`product-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {p.image_url ? (
                      <img src={imgUrl(p.image_url)} alt={p.name} className="h-10 w-10 rounded-md border border-slate-200 object-cover" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-400"><Package className="h-5 w-5" /></div>
                    )}
                    <Link to={`/inventory/product/${p.id}`} className="font-medium text-indigo-700 hover:text-indigo-900 hover:underline" data-testid={`product-link-${p.id}`}>{p.name}</Link>
                  </div>
                </TableCell>
                <TableCell>{p.sku || "-"}</TableCell>
                <TableCell>{p.unit}</TableCell>
                {showCost && <TableCell className="text-right">{money(p.cost_price)}</TableCell>}
                {showB2B && <TableCell className="text-right" data-testid={`b2b-price-${p.id}`}>{money(p.b2b_price)}</TableCell>}
                {showSell && <TableCell className="text-right">{money(p.sell_price)}</TableCell>}
                <TableCell className="text-right">
                  <Badge variant={qtyFor(p) > 0 ? "secondary" : "destructive"} data-testid={`stock-qty-${p.id}`}>{qtyFor(p)}</Badge>
                  {(p.low_warehouses || []).length > 0 && (
                    <div className="mt-0.5 text-[11px] font-medium text-amber-600" data-testid={`low-warehouse-${p.id}`}>
                      Low in: {p.low_warehouses.join(", ")}
                    </div>
                  )}
                </TableCell>
                {showValue && <TableCell className="text-right font-medium">{p.cost_price === null ? "—" : inr(qtyFor(p) * (p.cost_price || 0))}</TableCell>}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canMove && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openMovement(p, "in")} data-testid={`inbound-${p.id}`}>
                          <ArrowDownToLine className="mr-1 h-3.5 w-3.5 text-emerald-600" /> In
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openMovement(p, "out")} data-testid={`outbound-${p.id}`}>
                          <ArrowUpFromLine className="mr-1 h-3.5 w-3.5 text-rose-500" /> Out
                        </Button>
                      </>
                    )}
                    {canEditProduct && (
                      <Button variant="ghost" size="icon" onClick={() => { setProdForm({ ...emptyProduct, ...p }); setEditProdId(p.id); setProdOpen(true); }} data-testid={`edit-product-${p.id}`} aria-label="Edit product">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canManage && (
                      <Button variant="ghost" size="icon" onClick={() => removeProduct(p.id)} data-testid={`delete-product-${p.id}`} aria-label="Delete product">
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {visibleStock.length === 0 && (
              <TableRow><TableCell colSpan={5 + (showCost ? 1 : 0) + (showB2B ? 1 : 0) + (showSell ? 1 : 0) + (showValue ? 1 : 0)}><EmptyState compact title="No products yet" hint="Add your first product." /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination total={sortedStock.length} page={pg.page} size={pg.size} onChange={setPg} testid="inventory-pagination" />
      </div>

      <Tabs value={activityTab} onValueChange={setActivityTab}>
        <TabsList data-testid="activity-tabs">
          <TabsTrigger value="movements" data-testid="tab-movements">Movements (Last 2 Days)</TabsTrigger>
          {showSold && <TabsTrigger value="sold" data-testid="tab-sold">Sold Goods</TabsTrigger>}
        </TabsList>
      </Tabs>

      {activityTab === "movements" && (
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <span className="text-sm font-semibold text-slate-900">Stock Movements — Last 2 Days</span>
          <Input placeholder="Filter by tag, product, note..." value={movSearch} onChange={(e) => setMovSearch(e.target.value)} className="h-8 w-64" data-testid="movement-search-input" />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Warehouse</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              {["admin", "staff", "accountant"].includes(user?.role) && <TableHead className="text-right">Rate</TableHead>}
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="movements-table">
            {recentMovements.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{String(m.created_at).slice(0, 10)}</TableCell>
                <TableCell className="font-medium">{m.product_name}</TableCell>
                <TableCell>
                  <Badge className={m.type === "in" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}>
                    {m.type === "in" ? "Inbound" : "Outbound"}
                  </Badge>
                </TableCell>
                <TableCell>{warehouses.find((w) => w.id === m.warehouse_id)?.name || "-"}</TableCell>
                <TableCell>{m.tag ? <Badge variant="outline" style={tagStyle(m.tag)} data-testid={`movement-tag-${m.id}`}>{m.tag}</Badge> : "-"}</TableCell>
                <TableCell className="text-right">{m.qty}</TableCell>
                {["admin", "staff", "accountant"].includes(user?.role) && <TableCell className="text-right">{money(m.rate)}</TableCell>}
                <TableCell className="max-w-40 truncate">{m.note || "-"}</TableCell>
              </TableRow>
            ))}
            {recentMovements.length === 0 && (
              <TableRow><TableCell colSpan={8}><EmptyState compact title="No movements in the last 2 days" /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      )}

      {showSold && activityTab === "sold" && (
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Sold Goods</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Warehouse</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="sales-table">
            {sales.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{String(s.created_at).slice(0, 10)}</TableCell>
                <TableCell className="font-medium">{s.product_name}</TableCell>
                <TableCell>{warehouses.find((w) => w.id === s.warehouse_id)?.name || "-"}</TableCell>
                <TableCell>{s.tag ? <Badge variant="outline" style={tagStyle(s.tag)}>{s.tag}</Badge> : "-"}</TableCell>
                <TableCell className="text-right">{s.qty}</TableCell>
                <TableCell className="text-right">{s.rate == null ? "—" : inr(s.rate)}</TableCell>
                <TableCell className="text-right font-medium">{s.total == null ? "—" : inr(s.total)}</TableCell>
                <TableCell className="max-w-40 truncate">{s.note || "-"}</TableCell>
              </TableRow>
            ))}
            {sales.length === 0 && (
              <TableRow><TableCell colSpan={8}><EmptyState compact title="No sales recorded yet" hint='Use Outbound → "Sold to customer".' /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      )}

      </>)}

      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle>Add Warehouse</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} placeholder="e.g. Bhiwandi Warehouse" className="mt-1.5" data-testid="warehouse-name-input" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={whForm.location} onChange={(e) => setWhForm({ ...whForm, location: e.target.value })} className="mt-1.5" data-testid="warehouse-location-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={addWarehouse} data-testid="save-warehouse-button">Add Warehouse</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editProdId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 flex items-center gap-3">
              {prodForm.image_url ? (
                <img src={imgUrl(prodForm.image_url)} alt="product" className="h-16 w-16 rounded-md border border-slate-200 object-cover" data-testid="product-image-preview" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-md bg-slate-100 text-slate-400"><ImagePlus className="h-6 w-6" /></div>
              )}
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} data-testid="product-image-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-image-button">Upload Image</Button>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} className="mt-1.5" data-testid="product-name-input" />
            </div>
            <div>
              <Label>SKU</Label>
              <Input value={prodForm.sku} onChange={(e) => setProdForm({ ...prodForm, sku: e.target.value })} className="mt-1.5" data-testid="product-sku-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Input list="product-categories" value={prodForm.category} onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })} placeholder="e.g. Kitchenware" className="mt-1.5" data-testid="product-category-input" />
              <datalist id="product-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <Label>Unit</Label>
              <Input list="product-units" value={prodForm.unit} onChange={(e) => setProdForm({ ...prodForm, unit: e.target.value })} placeholder="pcs / box / kg" className="mt-1.5" data-testid="product-unit-input" />
              <datalist id="product-units">{units.map((u) => <option key={u} value={u} />)}</datalist>
            </div>
            <div>
              <Label>Pcs/Ctn (pieces per carton)</Label>
              <Input type="number" min="0" value={prodForm.pcs_per_ctn} onChange={(e) => setProdForm({ ...prodForm, pcs_per_ctn: e.target.value })} placeholder="0 = not sold in cartons" className="mt-1.5" data-testid="product-pcs-per-ctn-input" />
            </div>
            <div>
              <Label>Min stock alert (per warehouse)</Label>
              <Input type="number" min="0" value={prodForm.min_stock} onChange={(e) => setProdForm({ ...prodForm, min_stock: e.target.value })} placeholder="0 = off" className="mt-1.5" data-testid="product-min-stock-input" />
            </div>
            {showCost && (
            <div>
              <Label>Cost Price (₹)</Label>
              <Input type="number" value={prodForm.cost_price} onChange={(e) => setProdForm({ ...prodForm, cost_price: e.target.value })} className="mt-1.5" data-testid="product-cost-input" />
            </div>
            )}
            {showB2B && (
              <div>
                <Label>B2B Price (transfer to Company 2)</Label>
                <Input type="number" value={prodForm.b2b_price} onChange={(e) => setProdForm({ ...prodForm, b2b_price: e.target.value })} className="mt-1.5" data-testid="product-b2b-input" />
              </div>
            )}
            {showSell && (
            <div>
              <Label>Selling Price (₹)</Label>
              <Input type="number" value={prodForm.sell_price} onChange={(e) => setProdForm({ ...prodForm, sell_price: e.target.value })} className="mt-1.5" data-testid="product-sell-input" />
            </div>
            )}
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="mt-1.5" data-testid="product-description-input" />
            </div>
          </div>
          <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={saveProduct} data-testid="save-product-button">{editProdId ? "Save Changes" : "Add Product"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={movOpen} onOpenChange={setMovOpen}>
        <DialogContent data-testid="movement-dialog">
          <DialogHeader>
            <DialogTitle>{movForm.type === "in" ? "Record Inbound" : "Record Outbound"}</DialogTitle>
          </DialogHeader>
          {(() => {
            const mp = stock.find((p) => p.id === movForm.product_id);
            if (!mp) return null;
            const inWh = movForm.warehouse_id ? (mp.stock_by_warehouse?.[movForm.warehouse_id] ?? 0) : mp.total_stock;
            return (
              <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="movement-product-info">
                {mp.image_url ? (
                  <img src={imgUrl(mp.image_url)} alt={mp.name} className="h-10 w-10 rounded-md border border-slate-200 object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-200 text-slate-400"><Package className="h-5 w-5" /></div>
                )}
                <div>
                  <div className="text-sm font-semibold text-slate-900">{mp.name}</div>
                  <div className="text-xs text-slate-500">{mp.sku ? `SKU: ${mp.sku} · ` : ""}Current stock: {inWh} {mp.unit}{(mp.pcs_per_ctn || 0) > 0 ? ` · 1 ctn = ${mp.pcs_per_ctn} pcs` : ""}</div>
                </div>
              </div>
            );
          })()}
          <div className="space-y-4">
            <div>
              <Label>Warehouse</Label>
              <Select value={movForm.warehouse_id} onValueChange={(v) => setMovForm({ ...movForm, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="movement-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {movForm.type === "out" && (
              <div>
                <Label>Stock is being</Label>
                <Select value={movForm.out_type} onValueChange={(v) => setMovForm({ ...movForm, out_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="movement-out-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sold" data-testid="out-type-sold">Sold to Amazon</SelectItem>
                    <SelectItem value="sold_to_b2b" data-testid="out-type-b2b">Sold to B2B (Company 2)</SelectItem>
                    <SelectItem value="transfer">Transferred to another warehouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {movForm.type === "out" && movForm.out_type === "transfer" && (
              <div>
                <Label>Transfer To</Label>
                <Select value={movForm.transfer_to} onValueChange={(v) => setMovForm({ ...movForm, transfer_to: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="movement-transfer-select"><SelectValue placeholder="Select destination warehouse" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.filter((w) => w.id !== movForm.warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={`grid gap-3 ${showMovRate ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Quantity</Label>
                  {(stock.find((p) => p.id === movForm.product_id)?.pcs_per_ctn || 0) > 0 && (
                    <div className="flex rounded-md border border-slate-200 p-0.5" data-testid="movement-unit-toggle">
                      {["", "ctn"].map((m) => (
                        <button
                          key={m || "base"}
                          onClick={() => setMovForm({ ...movForm, unit_mode: m })}
                          className={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors duration-150 ${movForm.unit_mode === m || (!movForm.unit_mode && !m) ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
                          data-testid={`movement-unit-${m || "pcs"}`}
                        >
                          {m === "ctn" ? "Carton" : (stock.find((p) => p.id === movForm.product_id)?.unit || "pcs")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input type="number" min="1" value={movForm.qty} onChange={(e) => setMovForm({ ...movForm, qty: e.target.value })} className="mt-1.5" data-testid="movement-qty-input" />
                {movForm.unit_mode === "ctn" && (
                  <p className="mt-1 text-[11px] font-medium text-indigo-600" data-testid="movement-ctn-calc">
                    = {(Number(movForm.qty) || 0) * (stock.find((p) => p.id === movForm.product_id)?.pcs_per_ctn || 0)} pcs will be {movForm.type === "in" ? "added" : "reduced"}
                  </p>
                )}
              </div>
              {showMovRate && movForm.out_type !== "sold_to_b2b" && (
              <div>
                <Label>Rate (₹)</Label>
                <Input type="number" value={movForm.rate} onChange={(e) => setMovForm({ ...movForm, rate: e.target.value })} className="mt-1.5" data-testid="movement-rate-input" />
              </div>
              )}
            </div>
            <div>
              <Label>Tag</Label>
              <Input list="movement-tags" value={movForm.tag} onChange={(e) => setMovForm({ ...movForm, tag: e.target.value })} placeholder="Pick existing or type a new tag" className="mt-1.5" data-testid="movement-tag-input" />
              <datalist id="movement-tags">
                {tags.map((t) => <option key={t.name} value={t.name} />)}
              </datalist>
            </div>
            {movForm.tag.trim() && !tags.some((t) => t.name === movForm.tag.trim()) && (
              <div className="flex items-center gap-3 rounded-md border border-dashed border-slate-300 p-2.5" data-testid="new-tag-color-row">
                <Label className="text-xs">New tag color</Label>
                <Input type="color" value={tagColor} onChange={(e) => setTagColor(e.target.value)} className="h-8 w-14 p-1" data-testid="movement-tag-color" />
                <Badge variant="outline" style={{ backgroundColor: tagColor + "1A", color: tagColor, borderColor: tagColor + "66" }}>{movForm.tag.trim()}</Badge>
              </div>
            )}
            <div>
              <Label>Note</Label>
              <Input value={movForm.note} onChange={(e) => setMovForm({ ...movForm, note: e.target.value })} placeholder="PO no / client / reason" className="mt-1.5" data-testid="movement-note-input" />
            </div>
            <Button className={movForm.type === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} onClick={saveMovement} disabled={movSaving} data-testid="save-movement-button">
              {movSaving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {movSaving ? "Recording..." : `Confirm ${movForm.type === "in" ? "Inbound" : "Outbound"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
